import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Bell, Loader2, Ship, FileText, ShieldCheck, MessageSquare, PoundSterling, Mail, Building, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIFICATION_OPTIONS = [
  { key: 'new_trip', label: 'Client enters a new trip', icon: Ship, description: 'When a linked client creates or updates a trip record' },
  { key: 'new_document', label: 'Client uploads travel details', icon: FileText, description: 'When a client uploads a document (payslip, contract, etc.)' },
  { key: 'new_payslip', label: 'Client uploads a new payslip', icon: PoundSterling, description: 'Specifically when a wage slip or payslip is uploaded' },
  { key: 'sed_status_change', label: 'Client SED status changes', icon: ShieldCheck, description: 'When eligibility status changes (e.g. becomes eligible or loses eligibility)' },
  { key: 'client_message', label: 'Client sends a message', icon: MessageSquare, description: 'When a linked client sends you a direct message' },
];

const AccountantSettings = () => {
  const [prefs, setPrefs] = useState(null);
  const [practice, setPractice] = useState(null);
  const [practiceForm, setPracticeForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPractice, setSavingPractice] = useState(false);
  const [isPracticeAdmin, setIsPracticeAdmin] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prefsRes, practiceRes] = await Promise.all([
        axios.get(`${API_URL}/api/accountant/notification-preferences`),
        axios.get(`${API_URL}/api/accountant/practice`)
      ]);
      setPrefs(prefsRes.data);
      const p = practiceRes.data;
      setPractice(p);
      setIsPracticeAdmin(p?.is_practice_admin || false);
      setPracticeForm({
        firm_name: p?.firm_name || '',
        branch_address: p?.branch_address || '',
        phone: p?.phone || '',
        professional_body: p?.professional_body || '',
        membership_number: p?.membership_number || '',
      });
    } catch (err) {
      // silent
      setPrefs({
        new_trip: true, new_document: true, new_payslip: true,
        sed_status_change: true, client_message: true,
        email_digest: false, email_frequency: 'daily'
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePref = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/accountant/notification-preferences`, prefs);
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const savePractice = async () => {
    setSavingPractice(true);
    try {
      await axios.put(`${API_URL}/api/accountant/practice`, practiceForm);
      toast.success('Practice details updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update practice');
    } finally {
      setSavingPractice(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="h-6 w-6 animate-spin text-sky-400" /></div>;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="accountant-settings">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-slate-400 text-sm mt-1">Manage your practice profile and notification preferences</p>
      </div>

      {/* Practice Profile — Practice Admin only */}
      {isPracticeAdmin && practice && !practice.superadmin_view && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Building className="h-5 w-5 text-sky-400" />
              Practice Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Firm Name</Label>
                <Input value={practiceForm.firm_name || ''} onChange={e => setPracticeForm(p => ({...p, firm_name: e.target.value}))}
                  className="bg-slate-900 border-slate-600 text-white h-9 text-sm" data-testid="practice-firm-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Phone</Label>
                <Input value={practiceForm.phone || ''} onChange={e => setPracticeForm(p => ({...p, phone: e.target.value}))}
                  className="bg-slate-900 border-slate-600 text-white h-9 text-sm" data-testid="practice-phone" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Branch Address</Label>
              <Input value={practiceForm.branch_address || ''} onChange={e => setPracticeForm(p => ({...p, branch_address: e.target.value}))}
                className="bg-slate-900 border-slate-600 text-white h-9 text-sm" data-testid="practice-address" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Professional Body</Label>
                <Input value={practiceForm.professional_body || ''} onChange={e => setPracticeForm(p => ({...p, professional_body: e.target.value}))}
                  className="bg-slate-900 border-slate-600 text-white h-9 text-sm" data-testid="practice-body" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Membership Number</Label>
                <Input value={practiceForm.membership_number || ''} onChange={e => setPracticeForm(p => ({...p, membership_number: e.target.value}))}
                  className="bg-slate-900 border-slate-600 text-white h-9 text-sm" data-testid="practice-membership" />
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span>HMRC ARN: <span className="text-white">{practice?.hmrc_arn}</span></span>
              <span>Status: <span className={practice?.status === 'approved' ? 'text-emerald-400' : 'text-amber-400'}>{practice?.status}</span></span>
            </div>
            <Button onClick={savePractice} disabled={savingPractice}
              className="bg-sky-600 hover:bg-sky-700 h-9 text-sm" data-testid="save-practice-btn">
              {savingPractice ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save Practice Details
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Notification Preferences */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Bell className="h-5 w-5 text-sky-400" />
            In-App Notification Alerts
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {NOTIFICATION_OPTIONS.map(opt => (
            <div key={opt.key} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div className="flex items-center gap-3 min-w-0">
                <opt.icon className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-white">{opt.label}</p>
                  <p className="text-xs text-slate-500">{opt.description}</p>
                </div>
              </div>
              <Switch
                checked={prefs?.[opt.key] ?? true}
                onCheckedChange={() => togglePref(opt.key)}
                data-testid={`toggle-${opt.key}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Email Digest */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Mail className="h-5 w-5 text-sky-400" />
            Email Digest
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
            <div>
              <p className="text-sm text-white">Receive email summary</p>
              <p className="text-xs text-slate-500">Get a periodic email summary of client activity</p>
            </div>
            <Switch
              checked={prefs?.email_digest ?? false}
              onCheckedChange={() => togglePref('email_digest')}
              data-testid="toggle-email-digest"
            />
          </div>
          {prefs?.email_digest && (
            <div className="flex items-center gap-3 pl-3">
              <Label className="text-slate-400 text-sm">Frequency:</Label>
              <div className="flex gap-2">
                {['daily', 'weekly'].map(freq => (
                  <button key={freq} onClick={() => setPrefs(p => ({ ...p, email_frequency: freq }))}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                      prefs?.email_frequency === freq ? 'bg-sky-600 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`} data-testid={`freq-${freq}`}>
                    {freq.charAt(0).toUpperCase() + freq.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Button onClick={savePrefs} disabled={saving}
        className="bg-sky-600 hover:bg-sky-700 h-10 w-full sm:w-auto" data-testid="save-prefs-btn">
        {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save Preferences'}
      </Button>
    </div>
  );
};

export default AccountantSettings;
